/**
 * THE GUARANTEED INCOME IN A SUPPORT AT HOME PLAN (owner, 21 Oct 2026 — the guaranteed income review and the
 * agreed mock-up, docs/ux-review/annuities/ screens 3–6).
 *
 * One reading of "which guaranteed income does this plan buy, how much, for whom and of what type" for the
 * plan cards (the family panel and the income-only confirmation), the hand-built plan and the advice
 * document, so the three cannot describe different products.
 */
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { familyPanelKindOf, type FamilyPanelKind } from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { isIncomeOnlyAnnuity } from '../../components/strategy/IncomeOnlyTick.tsx';

export interface SahPlanAnnuity {
    amount: number;
    annuityType: string;
    residualCapital: number;
    owner: 'client' | 'partner' | 'joint';
    kind: FamilyPanelKind;
    /** Income only: a lifetime annuity where nothing comes back (IncomeOnlyTick.isIncomeOnlyAnnuity). */
    incomeOnly: boolean;
    /** The guaranteed income is the plan's only piece, so the support alone IS the same plan without it. */
    only: boolean;
}

type ConfigLike = { type?: unknown; steps?: unknown[]; [key: string]: unknown } | null | undefined;
type AnnuityLike = { type?: unknown; purchaseAmount?: unknown; annuityType?: unknown; residualCapital?: unknown; ownership?: unknown; owner?: unknown };

const ownerOf = (v: unknown): 'client' | 'partner' | 'joint' => (v === 'partner' ? 'partner' : v === 'joint' ? 'joint' : 'client');

function fromAnnuity(a: AnnuityLike | null | undefined, only: boolean): SahPlanAnnuity | null {
    const amount = Number(a?.purchaseAmount) || 0;
    if (!a || !(amount > 0)) return null;
    const annuityType = String(a.annuityType || 'lifetime');
    const residualCapital = Number.isFinite(Number(a.residualCapital)) ? Number(a.residualCapital) : 0;
    return {
        amount,
        annuityType,
        residualCapital,
        owner: ownerOf(a.ownership ?? a.owner),
        kind: familyPanelKindOf({ annuityType, residualCapital }),
        incomeOnly: isIncomeOnlyAnnuity({ purchaseAmount: amount, annuityType, residualCapital }),
        only,
    };
}

/** The guaranteed income a tested plan buys (its engine config, or one step of a combination), or null. */
export function sahPlanAnnuityOf(config: ConfigLike): SahPlanAnnuity | null {
    if (!config) return null;
    const steps = (config.type === 'combination' ? (config.steps || []) : [config]) as AnnuityLike[];
    const a = steps.find((s) => s?.type === 'annuity');
    return a ? fromAnnuity(a, steps.length === 1) : null;
}

/** The guaranteed income a hand-built plan buys (its annuity record), or null. `only`: no other piece is set. */
export function sahRecordAnnuityOf(record: AnnuityLike | null | undefined, only: boolean): SahPlanAnnuity | null {
    return fromAnnuity(record, only);
}

/** Who the guaranteed income is for, as the screens and the document name them. */
export function sahAnnuityPersonName(factFind: DraftFactFind | null | undefined, owner: 'client' | 'partner' | 'joint'): string {
    const c = factFind?.client?.firstName || 'the client';
    const p = factFind?.partner?.firstName || 'the partner';
    if (owner === 'partner') return p;
    if (owner === 'joint' && factFind?.adviceFor === 'couple') return `${c} and ${p}`;
    return c;
}

type Row = { assets?: { annuities?: Array<{ id?: unknown; annualPayment?: unknown }> } | Record<string, unknown> };

/** What the plan's own guaranteed income pays a year, read off its first projection row (the figure the projection ran on). */
export function planAnnuityIncomeOf(rows: readonly unknown[] | null | undefined, factFind: DraftFactFind | null | undefined): number | null {
    const first = (rows || [])[0] as Row | undefined;
    const list = ((first?.assets as { annuities?: Array<{ id?: unknown; annualPayment?: unknown }> } | undefined)?.annuities) || [];
    const onFile = new Set((((factFind?.assets as { annuities?: Array<{ id?: unknown }> } | undefined)?.annuities) || []).map((a) => String(a?.id)));
    const total = list.filter((a) => !onFile.has(String(a?.id))).reduce((t, a) => t + (Number(a?.annualPayment) || 0), 0);
    return total > 0 ? total : null;
}

/** The compare sentence under the family table, where the support alone is the same plan without it. */
export const SAH_COMPARE_NOTE = 'the same plan without it, the money left in savings';
